<!-- AgenciesManagementPanel.vue -->
<!-- Componente para presentación de las agencias supervisadas -->

<!--
  MGI-PILOT-W2-X2-FS-LANDING-CANONICAL-DATA-R1 — HONEST CATALOGUE.

  This panel used to declare seven columns and four status summary cards. Four of those columns
  (Estado, Última Visita, Issues, Responsable) and the whole status vocabulary of the summary cards
  had NO source authority: the canonical server-scoped reader `GET /api/groups/with-agencies`
  carries `id`, `name`, `address` and the owning `groupId` per agency and nothing else about
  operation, visits, incidences or people. The absent values were not rendered as gaps — they were
  published as facts: an agency with no status showed the green chip "Operativa", an agency with no
  incidence data showed "Sin issues", an agency with no manager showed "No asignado", and an agency
  never visited showed a red "Nunca" indistinguishable from a genuinely overdue visit.

  The panel now renders exactly what the canonical reader authorises — agency, group, address — and
  the write affordances (edit / change status / create route) are ABSENT unless the consumer asks
  for them with `canEdit`. Restoring a retired column requires a reader that actually measures it.
-->

<template>
  <div class="agencies-management-panel">
    <!-- Tabla de agencias supervisadas -->
    <v-data-table
      :headers="agencyHeaders"
      :items="agencies"
      :loading="loading"
      class="elevation-1"
      :items-per-page="10"
      data-testid="agencies-table"
    >
      <template #top>
        <v-toolbar flat>
          <v-toolbar-title>Agencias supervisadas</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="canEdit"
            color="primary"
            data-testid="agencies-create-route"
            @click="$emit('create-route', { agencies: selectedAgencies })"
            :disabled="selectedAgencies.length === 0"
          >
            <v-icon start>mdi-map-marker-path</v-icon>
            Crear Ruta
          </v-btn>
        </v-toolbar>
      </template>

      <template #item.groupName="{ item }">
        <span data-testid="agency-group">{{ item.groupName || '—' }}</span>
      </template>

      <template #item.address="{ item }">
        <span data-testid="agency-address">{{ item.address || '—' }}</span>
      </template>

      <template #item.actions="{ item }">
        <v-btn-group variant="text" size="small">
          <v-btn
            v-if="canEdit"
            icon="mdi-pencil"
            data-testid="agency-edit"
            @click="$emit('edit-agency', item)"
          />
          <v-btn
            icon="mdi-eye"
            data-testid="agency-details"
            @click="viewAgencyDetails(item)"
          />
          <v-menu v-if="canEdit">
            <template #activator="{ props }">
              <v-btn
                icon="mdi-dots-vertical"
                data-testid="agency-status-menu"
                v-bind="props"
              />
            </template>
            <v-list>
              <v-list-item
                v-for="status in statusOptions"
                :key="status.value"
                @click="$emit('change-status', item.id, status.value)"
              >
                <template #prepend>
                  <v-icon :color="status.color" :icon="status.icon"></v-icon>
                </template>
                <v-list-item-title>{{ status.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-btn-group>
      </template>
    </v-data-table>

    <!-- Dialog de detalles de agencia — sólo campos con autoridad canónica -->
    <v-dialog v-model="showDetailsDialog" max-width="600px">
      <v-card v-if="selectedAgency">
        <v-card-title>
          <span class="text-h5">{{ selectedAgency.name }}</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                label="Grupo"
                :model-value="selectedAgency.groupName || '—'"
                readonly
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                label="Dirección"
                :model-value="selectedAgency.address || '—'"
                readonly
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showDetailsDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineEmits } from 'vue'

/**
 * The shape the canonical scoped reader authorises, and nothing more. `address` is nullable because
 * the server row is; a missing address renders as a dash, never as an invented one.
 */
interface Agency {
  id: number
  name: string
  address?: string | null
  groupId?: number
  groupName?: string | null
}

interface Props {
  agencies: Agency[]
  canEdit: boolean
  loading?: boolean
}

defineProps<Props>()
defineEmits(['edit-agency', 'change-status', 'create-route'])

const selectedAgencies = ref<Agency[]>([])
const showDetailsDialog = ref(false)
const selectedAgency = ref<Agency | null>(null)

const agencyHeaders = computed(() => [
  { title: 'Agencia', key: 'name', sortable: true },
  { title: 'Grupo', key: 'groupName', sortable: true },
  { title: 'Dirección', key: 'address' },
  { title: 'Acciones', key: 'actions', sortable: false }
])

/** Only reachable while `canEdit` renders the status menu. */
const statusOptions = computed(() => [
  { value: 'operational', label: 'Operativa', icon: 'mdi-check-circle', color: 'success' },
  { value: 'maintenance', label: 'Mantenimiento', icon: 'mdi-tools', color: 'warning' },
  { value: 'closed', label: 'Cerrada', icon: 'mdi-close-circle', color: 'error' },
  { value: 'pending', label: 'Pendiente', icon: 'mdi-clock', color: 'info' }
])

const viewAgencyDetails = (agency: Agency) => {
  selectedAgency.value = agency
  showDetailsDialog.value = true
}
</script>

<style scoped>
.agencies-management-panel {
  width: 100%;
}
</style>
